import {
  BugOutlined,
  DashboardOutlined,
  HeartOutlined,
  LineChartOutlined,
  LoadingOutlined,
  MedicineBoxOutlined,
  MinusOutlined,
  MoonFilled,
  PlusOutlined,
  SunOutlined,
  TeamOutlined,
  ZoomInOutlined,
} from '@ant-design/icons';
import { Select, Tooltip } from 'antd';
import { Routes } from 'config/routes';
import { UserRole } from 'gql/types';
import CardTabs, { ITabsItem } from 'modules/common/components/CardTabs';
import { useZoom } from 'modules/common/hooks/useZoom';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useOperationsDashboardContext } from '../../contexts/OperationsDashboardContext';
import styles from '../OperationsDashboard/ops-theme.module.css';
import OpsDrilldownTab from '../OpsDrilldownTab';
import OpsHandlerTab from '../OpsHandlerTab';
import OpsOverviewTab from '../OpsOverviewTab';
import OpsPawsTab from '../OpsPawsTab';
import OpsPhlebotomistTab from '../OpsPhlebotomistTab';
import OpsRecruiterTab from '../OpsRecruiterTab';
import OpsTrendsTab from '../OpsTrendsTab';

const TabLabel: FC<{ text: string; icon: ReactNode }> = ({ text, icon }) => (
  <>
    <span className={styles.tabText}>{text}</span>
    <span className={styles.tabIcon}>{icon}</span>
  </>
);

// Map route keys to tab visibility keys (static)
const ROUTE_TO_TAB_KEY: Record<string, string> = {
  [Routes.DashboardsOperationsOverview]: 'overview',
  [Routes.DashboardsOperationsPhlebotomist]: 'phlebotomist',
  [Routes.DashboardsOperationsRecruiter]: 'recruiter',
  [Routes.DashboardsOperationsHandler]: 'handler',
  [Routes.DashboardsOperationsDrilldown]: 'drilldown',
  [Routes.DashboardsOperationsTrends]: 'trends',
  [Routes.DashboardsOperationsPaws]: 'paws',
};

const OperationsDashboardTabs: FC = () => {
  const { t } = useTranslation('dashboard.OperationsDashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<string>(location.pathname);
  const {
    isDarkMode,
    toggleTheme,
    opsSettings,
    effectiveRole,
    lastDrilldownUrl,
    containerWidth,
  } = useOperationsDashboardContext();
  const {
    zoomLevel,
    zoomIn,
    zoomOut,
    resetZoom,
    isZooming,
    MIN_ZOOM,
    MAX_ZOOM,
  } = useZoom();

  useEffect(() => {
    setActiveTab(location.pathname);
  }, [location.pathname]);

  const onChangeTabs = (tabKey: string) => {
    // When clicking drilldown tab, restore last drilldown URL if available
    if (tabKey === Routes.DashboardsOperationsDrilldown && lastDrilldownUrl) {
      navigate(lastDrilldownUrl);
      return;
    }
    navigate(tabKey);
  };

  const showPawsTab = effectiveRole === UserRole.Admin && opsSettings.showDebug;

  const allTabs: (ITabsItem & { label: string })[] = useMemo(
    () => [
      {
        title: (
          <TabLabel text={t('tabOverview')} icon={<DashboardOutlined />} />
        ),
        label: t('tabOverview'),
        content: <OpsOverviewTab />,
        key: Routes.DashboardsOperationsOverview,
      },
      {
        title: (
          <TabLabel
            text={t('tabPhlebotomist')}
            icon={<MedicineBoxOutlined />}
          />
        ),
        label: t('tabPhlebotomist'),
        content: <OpsPhlebotomistTab />,
        key: Routes.DashboardsOperationsPhlebotomist,
      },
      {
        title: <TabLabel text={t('tabRecruiter')} icon={<TeamOutlined />} />,
        label: t('tabRecruiter'),
        content: <OpsRecruiterTab />,
        key: Routes.DashboardsOperationsRecruiter,
      },
      {
        title: <TabLabel text={t('tabHandler')} icon={<HeartOutlined />} />,
        label: t('tabHandler'),
        content: <OpsHandlerTab />,
        key: Routes.DashboardsOperationsHandler,
      },
      {
        title: <TabLabel text={t('tabDrilldown')} icon={<ZoomInOutlined />} />,
        label: t('tabDrilldown'),
        content: <OpsDrilldownTab />,
        key: Routes.DashboardsOperationsDrilldown,
      },
      {
        title: <TabLabel text={t('tabTrends')} icon={<LineChartOutlined />} />,
        label: t('tabTrends'),
        content: <OpsTrendsTab />,
        key: Routes.DashboardsOperationsTrends,
      },
      ...(showPawsTab
        ? [
            {
              title: <TabLabel text={t('tabPaws')} icon={<BugOutlined />} />,
              label: t('tabPaws'),
              content: <OpsPawsTab />,
              key: Routes.DashboardsOperationsPaws,
            },
          ]
        : []),
    ],
    [t, showPawsTab]
  );

  // Filter tabs based on tabVisibility settings and effective role
  const opsCardTabs = useMemo(() => {
    if (!effectiveRole) return allTabs;
    const { tabVisibility } = opsSettings;
    return allTabs.filter((tab) => {
      const tabKey = ROUTE_TO_TAB_KEY[tab.key];
      if (!tabKey) return true;
      const visibility = tabVisibility[tabKey]?.[effectiveRole];
      return visibility !== 'hidden';
    });
  }, [effectiveRole, opsSettings, allTabs]);

  const isNarrow = containerWidth !== null && containerWidth < 550;

  const extraControls = (
    <div className={styles.tabBarExtra}>
      {opsSettings.showDebug && containerWidth !== null && (
        <span className={styles.containerWidthDebug}>{containerWidth}px</span>
      )}
      <div className={styles.zoomControls}>
        <button
          className={styles.zoomBtn}
          onClick={zoomOut}
          disabled={zoomLevel <= MIN_ZOOM}
          title="Decrease font size"
        >
          <MinusOutlined />
        </button>
        <Tooltip title="Click to reset">
          <span className={styles.zoomLevel} onClick={resetZoom}>
            {isZooming ? (
              <LoadingOutlined spin />
            ) : zoomLevel >= 0 ? (
              `+${zoomLevel}`
            ) : (
              zoomLevel
            )}
          </span>
        </Tooltip>
        <button
          className={styles.zoomBtn}
          onClick={zoomIn}
          disabled={zoomLevel >= MAX_ZOOM}
          title="Increase font size"
        >
          <PlusOutlined />
        </button>
      </div>
      <button
        className={styles.themeToggle}
        onClick={toggleTheme}
        title={isDarkMode ? 'Light mode' : 'Dark mode'}
      >
        {isDarkMode ? <SunOutlined /> : <MoonFilled />}
      </button>
    </div>
  );

  if (isNarrow) {
    const activeTabItem = opsCardTabs.find((t) => t.key === activeTab);
    return (
      <div>
        <div className={styles.narrowTabBar}>
          <Select
            value={activeTab}
            onChange={onChangeTabs}
            className={styles.narrowTabSelect}
            dropdownClassName={styles.opsSelectDropdown}
          >
            {opsCardTabs.map((tab) => (
              <Select.Option key={tab.key} value={tab.key}>
                {tab.label}
              </Select.Option>
            ))}
          </Select>
          {extraControls}
        </div>
        <div className={styles.narrowTabContent}>{activeTabItem?.content}</div>
      </div>
    );
  }

  return (
    <CardTabs
      onChange={onChangeTabs}
      activeKey={activeTab}
      items={opsCardTabs}
      className={opsCardTabs.length <= 3 ? styles.fewTabs : undefined}
      tabBarExtraContent={extraControls}
    />
  );
};

export default OperationsDashboardTabs;
